import {Transfer} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import debounce from 'lodash.debounce';
import {Modal} from 'ui';
import {useLecture} from "hooks/Lectures/useLecture";
import {LectureProps} from 'components/Lectures/types';
import './styles.scss'

interface LectureTransferModalInterface {
    selectedLectures: number[];
    excludeKeys?: number[];
    // Lectures already attached to this program are left out of the list.
    excludeProgramId?: number;
    visible?: boolean | undefined;
    oneWay?: boolean | undefined;

    onCancel(cancel: boolean): void;

    onOk(data: number[]): void;
}

interface TransferRecordType {
    key: string;
    title: string;
    disabled?: boolean;
}

type LectureTitleTranslationsResponse = Record<string, Record<string, string>>;

const LectureTransferModal: React.FC<LectureTransferModalInterface> = ({
                                                                           selectedLectures,
                                                                           excludeKeys,
                                                                           excludeProgramId,
                                                                           visible = false,
                                                                           oneWay = false,
                                                                           onCancel,
                                                                           onOk
                                                                       }) => {
    const [targetKeys, setTargetKeys] = useState<string[]>([]);
    const [lectures, setLectures] = useState<TransferRecordType[]>([]);
    const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

    const {getLectures, searchLectures, lectureLoading} = useLecture();
    const intl = useIntl();

    const getTranslationTitle = (translations: Record<string, string>): string => {
        const localeKey = intl.locale.toUpperCase();
        return (
            translations[localeKey] ??
            Object.values(translations)[0] ??
            ''
        );
    };

    // Newest lecture first. The API keys its response by lecture id, and JS objects
    // iterate integer-like keys in ascending order, so the sort must happen here.
    const sortByIdDesc = (records: TransferRecordType[]): TransferRecordType[] => {
        return [...records].sort((a, b) => Number(b.key) - Number(a.key));
    };

    const toTransferRecordsFromTranslations = (data: LectureTitleTranslationsResponse): TransferRecordType[] => {
        return sortByIdDesc(Object.entries(data).flatMap(([lectureId, translations]) => {
            const numericId = Number(lectureId);
            if (Number.isNaN(numericId)) return [];
            if (excludeKeys && excludeKeys.includes(numericId)) return [];

            return [{
                key: lectureId,
                title: getTranslationTitle(translations),
            }];
        }));
    };

    const toTransferRecordsFromLectures = (data: LectureProps[]): TransferRecordType[] => {
        return sortByIdDesc(data.flatMap((lecture) => {
            const numericId = lecture.id;
            if (!numericId) return [];
            if (excludeKeys && excludeKeys.includes(numericId)) return [];

            return [{
                key: String(numericId),
                title: lecture.title ?? getTranslationTitle(lecture.titleTranslations ?? {}),
            }];
        }));
    };

    const applyResponse = (response: unknown) => {
        if (!response) {
            setLectures([]);
            return;
        }

        if (Array.isArray(response)) {
            setLectures(toTransferRecordsFromLectures(response as LectureProps[]));
            return;
        }

        if (typeof response === 'object' && Object.keys(response as object).length) {
            setLectures(toTransferRecordsFromTranslations(response as LectureTitleTranslationsResponse));
            return;
        }

        setLectures([]);
    };

    useEffect(() => {
        if (!visible) {
            return;
        }

        const loadLectures = async () => {
            const response = await getLectures(excludeProgramId);
            applyResponse(response);
        }

        if (selectedLectures.length) {
            setTargetKeys(selectedLectures.map(String));
        }

        setConfirmLoading(false);
        loadLectures();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible])

    const debouncedSearch = useMemo(
        () => debounce(async (q: string) => {
            const response = q
                ? await searchLectures(q, excludeProgramId)
                : await getLectures(excludeProgramId);
            applyResponse(response);
        }, 400),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [excludeProgramId]
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearch = (direction: 'left' | 'right', value: string) => {
        if (direction !== 'left') return;
        debouncedSearch(value);
    };

    const handleOnConfirm = () => {
        if (confirmLoading) return;
        setConfirmLoading(true);
        try {
            onOk(targetKeys.map(Number).filter((id) => !Number.isNaN(id)));
        } catch (e) {
            setConfirmLoading(false);
            throw e;
        }
    }

    return (
        <Modal
            width={'80%'}
            open={visible}
            isLoading={lectureLoading || confirmLoading}
            onCancel={() => onCancel(false)}
            title={intl.formatMessage({id: 'general.lectures.add_lecture'})}
            okText={intl.formatMessage({id: 'general.submit'})}
            cancelText={intl.formatMessage({id: 'general.cancel'})}
            onOk={handleOnConfirm}
            destroyOnClose
        >
            <Transfer className='lectures-modal-transfer'
                      locale={{notFoundContent: intl.formatMessage({id: "general.found_no_data"})}}
                      dataSource={lectures}
                      showSearch
                      filterOption={() => true}
                      showSelectAll={true}
                      targetKeys={targetKeys}
                      onChange={(keys) => setTargetKeys(keys)}
                      onSearch={handleSearch}
                      render={(item: TransferRecordType) => item.title}
                      oneWay={oneWay}
                      pagination={{pageSize: 50}}
            />
        </Modal>
    )
};
export default LectureTransferModal;
